import React, { useId } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, ClipboardList, Plus, Trash2 } from 'lucide-react';
import type {
  StandardizedTestRowDto,
  TherapyDiscipline,
  TherapyTestFieldsDto,
} from '../../../dtos/patientmr/assessments/therapy-tests.dto';
import {
  MAX_NOT_TESTED_REASON,
  MAX_TEST_ROWS,
  THERAPY_INSTRUMENTS,
  emptyTestRow,
  isBelowThreshold,
  rowErrors,
  sdFromMean,
  timeErrors,
} from './standardizedTests';

export interface StandardizedTestTableProps {
  discipline: TherapyDiscipline;
  value: TherapyTestFieldsDto;
  onChange: (patch: Partial<TherapyTestFieldsDto>) => void;
  readOnly?: boolean;
  /** Values from the old free-text fields, shown read-only so nothing is lost */
  legacy?: Array<{ label: string; value: string }>;
}

type NumericField = 'rawScore' | 'standardScore' | 'mean' | 'sd' | 'percentile';

const INPUT = 'w-full min-h-[44px] p-2 border border-gray-300 rounded text-base bg-white';
const LABEL = 'block text-sm font-medium text-gray-700 mb-1 text-left';

const NUMERIC_FIELDS: Array<{ field: NumericField; label: string; min?: number; max?: number; step?: string }> = [
  { field: 'rawScore', label: 'Raw score', step: 'any' },
  { field: 'standardScore', label: 'Standard score', min: 0, max: 300, step: 'any' },
  { field: 'mean', label: 'Mean', min: 0, max: 300, step: 'any' },
  { field: 'sd', label: 'SD', min: 0, max: 100, step: 'any' },
  { field: 'percentile', label: 'Percentile', min: 0, max: 100, step: 'any' },
];

const toNum = (s: string): number | null => {
  if (s.trim() === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const show = (v: number | string | null | undefined): string => (v === null || v === undefined || v === '' ? '—' : String(v));

const SdBadge: React.FC<{ value: number | null }> = ({ value }) => {
  if (value === null) return <span className="text-gray-500">—</span>;
  const below = isBelowThreshold(value);
  return (
    <span className="inline-flex items-center gap-2" data-testid="sd-from-mean">
      <span className={below ? 'font-semibold text-red-700' : 'text-gray-900'}>{value} SD</span>
      {below && (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
          <AlertTriangle size={12} aria-hidden="true" />
          ≤ −1.5 SD
        </span>
      )}
    </span>
  );
};

/**
 * Phase 19 F23: standardized test results on a PT/OT/ST evaluation, the reason when no test was
 * given, time in/out and the THSteps attestation. "SD from mean" is computed live with the same
 * formula as the server: (standard score − mean) / SD, rounded to 2 places.
 */
const StandardizedTestTable: React.FC<StandardizedTestTableProps> = ({ discipline, value, onChange, readOnly = false, legacy = [] }) => {
  const baseId = useId();
  const rows = value.standardizedTests ?? [];
  const instruments = THERAPY_INSTRUMENTS[discipline];

  const setRows = (next: StandardizedTestRowDto[]) => onChange({ standardizedTests: next });
  const updateRow = (index: number, patch: Partial<StandardizedTestRowDto>) => {
    setRows(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };
  const addRow = () => {
    if (rows.length < MAX_TEST_ROWS) setRows([...rows, emptyTestRow()]);
  };
  const removeRow = (index: number) => setRows(rows.filter((_, i) => i !== index));

  const times = timeErrors(value);

  const legacyBlock = legacy.length > 0 && (
    <div className="rounded border border-gray-200 bg-gray-50 p-3" data-testid="legacy-test-entry">
      <p className="text-sm font-medium text-gray-700">Earlier entry</p>
      <dl className="mt-1 space-y-1">
        {legacy.map(e => (
          <div key={e.label} className="text-sm">
            <dt className="inline font-medium text-gray-600">{e.label}: </dt>
            <dd className="inline whitespace-pre-wrap text-gray-900">{e.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );

  if (readOnly) {
    return (
      <div className="space-y-4" data-testid="standardized-tests">
        {rows.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-left border border-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Instrument', 'Subtest', 'Raw', 'Standard', 'Mean', 'SD', 'SD from mean', 'Percentile', 'Age equiv.', 'Test date'].map(h => (
                    <th key={h} scope="col" className="px-2 py-2 font-medium text-gray-700 border-b border-gray-200 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="px-2 py-2">{show(r.instrument)}</td>
                    <td className="px-2 py-2">{show(r.subtest)}</td>
                    <td className="px-2 py-2">{show(r.rawScore)}</td>
                    <td className="px-2 py-2">{show(r.standardScore)}</td>
                    <td className="px-2 py-2">{show(r.mean)}</td>
                    <td className="px-2 py-2">{show(r.sd)}</td>
                    <td className="px-2 py-2 whitespace-nowrap"><SdBadge value={sdFromMean(r.standardScore, r.mean, r.sd)} /></td>
                    <td className="px-2 py-2">{show(r.percentile)}</td>
                    <td className="px-2 py-2">{show(r.ageEquivalent)}</td>
                    <td className="px-2 py-2 whitespace-nowrap">{show(r.testDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-600">No standardized test results recorded.</p>
        )}
        {value.notTestedReason?.trim() && (
          <p className="text-sm"><span className="font-medium text-gray-700">No standardized test given: </span><span className="whitespace-pre-wrap">{value.notTestedReason}</span></p>
        )}
        <p className="text-sm">
          <span className="font-medium text-gray-700">Time in: </span>{show(value.timeIn)}
          <span className="ml-4 font-medium text-gray-700">Time out: </span>{show(value.timeOut)}
        </p>
        <p className="text-sm">
          <span className="font-medium text-gray-700">THSteps checkup is current: </span>
          {value.thstepsCurrentAttested ? `Yes${value.thstepsAttestedDate ? ` (${value.thstepsAttestedDate})` : ''}` : 'No'}
        </p>
        {legacyBlock}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="standardized-tests">
      {rows.length === 0 && (
        <p className="text-sm text-gray-600">No test rows yet. Add a row for each instrument or subtest given.</p>
      )}

      {rows.map((row, index) => {
        const sd = sdFromMean(row.standardScore, row.mean, row.sd);
        const known = instruments.includes(row.instrument) && row.instrument !== 'Other';
        const selectValue = known ? row.instrument : row.instrument ? 'Other' : '';
        const errors = rowErrors(row);
        const idFor = (f: string) => `${baseId}-${index}-${f}`;
        return (
          <div key={index} role="group" className="rounded-lg border border-gray-200 bg-white p-3" aria-label={`Standardized test ${index + 1}`}>
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="text-sm font-semibold text-gray-800">Test {index + 1}</p>
              <button
                type="button"
                onClick={() => removeRow(index)}
                className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-1 rounded px-3 text-sm font-medium text-red-700 hover:bg-red-50"
                aria-label={`Remove test ${index + 1}`}
              >
                <Trash2 size={16} aria-hidden="true" />
                Remove
              </button>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="min-w-0">
                <label htmlFor={idFor('instrument')} className={LABEL}>Instrument<span className="ml-1 text-red-600">*</span></label>
                <select
                  id={idFor('instrument')}
                  className={INPUT}
                  value={selectValue}
                  onChange={(e) => updateRow(index, { instrument: e.target.value })}
                >
                  <option value="">Select instrument...</option>
                  {instruments.map(name => <option key={name} value={name}>{name}</option>)}
                </select>
              </div>
              {selectValue === 'Other' && (
                <div className="min-w-0">
                  <label htmlFor={idFor('other')} className={LABEL}>Instrument name</label>
                  <input
                    id={idFor('other')}
                    type="text"
                    maxLength={120}
                    className={INPUT}
                    value={row.instrument === 'Other' ? '' : row.instrument}
                    onChange={(e) => updateRow(index, { instrument: e.target.value.trim() === '' ? 'Other' : e.target.value })}
                    placeholder="Type the instrument"
                  />
                </div>
              )}
              <div className="min-w-0">
                <label htmlFor={idFor('subtest')} className={LABEL}>Subtest</label>
                <input
                  id={idFor('subtest')}
                  type="text"
                  maxLength={120}
                  className={INPUT}
                  value={row.subtest ?? ''}
                  onChange={(e) => updateRow(index, { subtest: e.target.value })}
                  placeholder="e.g., GMQ, Auditory Comprehension"
                />
              </div>
              {NUMERIC_FIELDS.map(({ field, label, min, max, step }) => (
                <div key={field} className="min-w-0">
                  <label htmlFor={idFor(field)} className={LABEL}>{label}</label>
                  <input
                    id={idFor(field)}
                    type="number"
                    inputMode="decimal"
                    min={min}
                    max={max}
                    step={step}
                    className={INPUT}
                    value={row[field] ?? ''}
                    onChange={(e) => updateRow(index, { [field]: toNum(e.target.value) })}
                  />
                </div>
              ))}
              <div className="min-w-0">
                <label htmlFor={idFor('age')} className={LABEL}>Age equivalent</label>
                <input
                  id={idFor('age')}
                  type="text"
                  maxLength={40}
                  className={INPUT}
                  value={row.ageEquivalent ?? ''}
                  onChange={(e) => updateRow(index, { ageEquivalent: e.target.value })}
                  placeholder="e.g., 14 months"
                />
              </div>
              <div className="min-w-0">
                <label htmlFor={idFor('date')} className={LABEL}>Test date</label>
                <input
                  id={idFor('date')}
                  type="date"
                  className={INPUT}
                  value={row.testDate ?? ''}
                  onChange={(e) => updateRow(index, { testDate: e.target.value })}
                />
              </div>
              <div className="min-w-0">
                <span className={LABEL}>SD from mean</span>
                <div className="flex min-h-[44px] items-center">
                  <SdBadge value={sd} />
                </div>
              </div>
            </div>
            {errors.length > 0 && (
              <ul className="mt-2 space-y-1 text-sm text-red-700" role="alert">
                {errors.map(e => <li key={e}>{e}</li>)}
              </ul>
            )}
          </div>
        );
      })}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={addRow}
          disabled={rows.length >= MAX_TEST_ROWS}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-md border border-gray-300 bg-white px-4 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Plus size={16} aria-hidden="true" />
          Add test
        </button>
        {rows.length >= MAX_TEST_ROWS && (
          <span className="text-sm text-gray-600">Up to {MAX_TEST_ROWS} test rows.</span>
        )}
      </div>

      <div>
        <label htmlFor={`${baseId}-reason`} className={LABEL}>No standardized test given (reason)</label>
        <textarea
          id={`${baseId}-reason`}
          rows={2}
          maxLength={MAX_NOT_TESTED_REASON}
          className="w-full min-h-[44px] p-2 border border-gray-300 rounded text-base"
          value={value.notTestedReason ?? ''}
          onChange={(e) => onChange({ notTestedReason: e.target.value })}
          placeholder="Why no standardized test was given (e.g., medically fragile, unable to participate)"
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor={`${baseId}-timein`} className={LABEL}>Time in</label>
          <input
            id={`${baseId}-timein`}
            type="time"
            className={INPUT}
            value={value.timeIn ?? ''}
            onChange={(e) => onChange({ timeIn: e.target.value })}
          />
        </div>
        <div>
          <label htmlFor={`${baseId}-timeout`} className={LABEL}>Time out</label>
          <input
            id={`${baseId}-timeout`}
            type="time"
            className={INPUT}
            value={value.timeOut ?? ''}
            onChange={(e) => onChange({ timeOut: e.target.value })}
          />
        </div>
      </div>
      {times.length > 0 && (
        <ul className="space-y-1 text-sm text-red-700" role="alert">
          {times.map(e => <li key={e}>{e}</li>)}
        </ul>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <label className="inline-flex min-h-[44px] items-center gap-3 text-sm text-gray-800">
          <input
            type="checkbox"
            className="h-5 w-5 rounded border-gray-300"
            checked={value.thstepsCurrentAttested === true}
            onChange={(e) => onChange({ thstepsCurrentAttested: e.target.checked })}
          />
          THSteps checkup is current
        </label>
        {value.thstepsCurrentAttested && (
          <div className="sm:w-56">
            <label htmlFor={`${baseId}-thsteps`} className={LABEL}>THSteps checkup date</label>
            <input
              id={`${baseId}-thsteps`}
              type="date"
              className={INPUT}
              value={value.thstepsAttestedDate ?? ''}
              onChange={(e) => onChange({ thstepsAttestedDate: e.target.value })}
            />
          </div>
        )}
      </div>

      {legacyBlock}
    </div>
  );
};

/** Section wrapper used by the PT/OT/ST assessment forms. */
export const StandardizedTestSection: React.FC<StandardizedTestTableProps & { collapsed: boolean; onToggle: () => void }> = ({
  collapsed,
  onToggle,
  ...tableProps
}) => (
  <div className="mb-6">
    <div className="rounded-lg border border-indigo-200 bg-indigo-50 shadow-sm">
      <button
        type="button"
        className="flex w-full min-h-[44px] items-center justify-between p-4 text-left hover:bg-indigo-100 rounded-t-lg transition-colors"
        onClick={onToggle}
        aria-expanded={!collapsed}
      >
        <span className="flex items-center">
          <ClipboardList size={18} className="mr-2 text-indigo-600" aria-hidden="true" />
          <span className="text-base font-medium text-gray-900">Standardized Tests</span>
          <span className="ml-2 text-xs text-red-600 font-medium">Required</span>
        </span>
        {collapsed ? <ChevronDown size={16} className="text-gray-500" aria-hidden="true" /> : <ChevronUp size={16} className="text-gray-500" aria-hidden="true" />}
      </button>
      {!collapsed && (
        <p className="text-sm text-gray-600 px-4 pb-2 border-b border-gray-100">
          Record each standardized test result, or the reason no standardized test was given. A score 1.5 SD or more below the mean supports a delay.
        </p>
      )}
      <div className={`p-4 ${collapsed ? 'hidden' : 'block'}`}>
        <StandardizedTestTable {...tableProps} />
      </div>
    </div>
  </div>
);

export default StandardizedTestTable;
